import { isEqual } from 'lodash';
import { useMemo, useState } from 'react';

import { Tooltip } from '@/components/tooltip';
import { Button, IconButton } from '@/components/button';
import { Form } from '@/components/form';
import { useDirtyContext } from '@/explore/dirty-context';
import type {
  CompositeFilterCondition,
  FilterOperation,
  VariableDefinition,
} from '@/explore/types';
import { useDeepCompareEffect } from '@/lib/hooks/use-deep-compare-hook';
import { FieldOrGroup, firstField, isField } from '@/explore/pipeline/utils';

import { createEmptyFilterCondition, isValidCondition } from '../utils/filter';
import { CompositeFilterConditionForm } from '.';

import form from '@/components/form/form.module.scss';

interface FilterFormProps {
  fields: FieldOrGroup[];
  operation?: FilterOperation;
  setOperation: (operation: FilterOperation) => void;
  onClose: () => void;
  onDuplicate?: (operation: FilterOperation) => void;
  variables: VariableDefinition[];
}

export const FilterForm = (props: FilterFormProps) => {
  const { operation } = props;
  const defaultCondition = useMemo(
    () => createEmptyFilterCondition(firstField(props.fields)),
    [props.fields],
  );
  const initialCondition = operation?.parameters ?? defaultCondition;
  const [condition, setCondition] = useState<CompositeFilterCondition>(initialCondition);
  const isValid = isValidCondition(condition, props.fields);

  const { setDirty } = useDirtyContext();

  useDeepCompareEffect(() => {
    if (operation?.parameters !== undefined) {
      setCondition(operation?.parameters);
    }
  }, [operation?.parameters]);

  const handleChange = (condition: CompositeFilterCondition) => {
    setCondition(condition);
    const isDirty = !isEqual(initialCondition, condition);
    setDirty(isDirty);
  };

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }
    setDirty(false);
    props.setOperation({
      operation: 'filter',
      parameters: condition,
    });
  };

  const handleCancel = () => {
    setDirty(false);
    props.onClose();
  };

  return (
    <Form className={form.formHorizontal} onSubmit={handleSubmit}>
      <CompositeFilterConditionForm
        fields={props.fields}
        fieldsForExpression={props.fields.filter(isField)}
        condition={condition}
        setCondition={handleChange}
        variables={props.variables}
      />
      <div className={form.formControls}>
        <Button size="small" type="submit" disabled={!isValid}>
          {props.operation ? 'Save' : 'Filter'}
        </Button>
        <Button size="small" variant="outlined" onClick={handleCancel}>
          {props.operation ? 'Cancel' : 'Back'}
        </Button>
        {props.onDuplicate !== undefined && (
          <>
            <div className={form.filler} />
            <Tooltip content="Duplicate step">
              <IconButton
                icon="Copy"
                onClick={() =>
                  props.onDuplicate?.({
                    operation: 'filter',
                    parameters: condition,
                  })
                }
              />
            </Tooltip>
          </>
        )}
      </div>
    </Form>
  );
};
